import { fetchText } from '../utils/http';

let cachedIndex = null;
let loadingPromise = null;

const fetchSearchIndex = async () => {
  if (cachedIndex) {
    return cachedIndex;
  }
  if (!loadingPromise) {
    loadingPromise = fetchText('/index.json')
      .then((raw) => {
        const payload = JSON.parse(raw);
        return payload?.data || [];
      })
      .catch((error) => {
        loadingPromise = null;
        throw error;
      });
  }
  cachedIndex = await loadingPromise;
  loadingPromise = null;
  return cachedIndex;
};

const normalise = (value = '') => value.toLowerCase();
const debug = (...args) => console.log('[siteSearch]', ...args);

export const registerSiteSearch = (Alpine) => {
  Alpine.data('siteSearch', () => ({
    query: '',
    results: [],
    status: 'idle',
    errorMessage: '',
    showDropdown: false,
    init() {
      debug('init');
      this.loadIndex();
    },
    async loadIndex() {
      if (this.status === 'loading' || this.status === 'ready') {
        debug('loadIndex: already loading/ready, skipping');
        return;
      }
      this.status = 'loading';
      try {
        debug('loadIndex: fetching /index.json');
        await fetchSearchIndex();
        debug('loadIndex: ready');
        this.status = 'ready';
      } catch (error) {
        console.error('[siteSearch] loadIndex error', error);
        this.status = 'error';
        this.errorMessage = error?.message || 'Unable to load search';
      }
    },
    get isReady() {
      return this.status === 'ready';
    },
    get placeholder() {
      if (this.status === 'loading') {
        return 'Loading…';
      }
      if (this.status === 'error') {
        return 'Search unavailable';
      }
      return 'Search…';
    },
    updateResults() {
      if (!this.isReady) {
        debug('updateResults: index not ready');
        return;
      }
      const query = normalise(this.query.trim());
      if (!query) {
        debug('updateResults: empty query');
        this.results = [];
        this.showDropdown = false;
        return;
      }
      const index = cachedIndex || [];
      debug('updateResults: filtering for', query, 'across', index.length, 'pages');
      this.results = index
        .filter((page) => {
          const titleMatch = normalise(page.title || '').includes(query);
          const tagMatch = (page.tags || []).some((tag) => normalise(tag).includes(query));
          return titleMatch || tagMatch;
        })
        .slice(0, 5);
      this.showDropdown = true;
      debug('updateResults: results ready', this.results.length);
    },
    focusResults() {
      debug('focusResults', { status: this.status, results: this.results.length });
      if (this.status === 'idle') {
        this.loadIndex();
      }
      if (this.results.length) {
        this.showDropdown = true;
      }
    },
    handleResultClick(payload) {
      const url = typeof payload === 'string'
        ? payload
        : payload?.target?.closest('a')?.getAttribute('href');
      debug('handleResultClick: resolved url', url, 'from payload', payload);
      if (!url) {
        debug('handleResultClick: missing url');
        return;
      }
      debug('handleResultClick: navigating', url);
      this.hideDropdown();
      window.location.assign(url);
    },
    hideDropdown() {
      debug('hideDropdown');
      this.showDropdown = false;
      this.results = [];
      this.query = '';
    },
    handleOutsideClick(event) {
      console.log('handleOutsideClick', event);
      if (!event) {
        debug('handleOutsideClick: missing event');
        return;
      }
      const dropdown = document.getElementById('search-results');
      const input = document.getElementById('search-input');
      const clickedInsideDropdown = dropdown?.contains(event.target);
      const clickedInput = input?.contains(event.target);
      if (clickedInsideDropdown || clickedInput) {
        debug('handleOutsideClick: click inside component, ignore');
        return;
      }
      debug('handleOutsideClick: closing dropdown');
      this.hideDropdown();
    },
  }));
};
